import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Check, Users } from 'lucide-react';
import { mockData } from '../mock';
import Popup from './Popup';

const MegaMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(mockData.serviceCategories[0]?.id || 1);

  // Get active category data
  const activeCategoryData = mockData.serviceCategories.find(
    cat => cat.id === activeCategory
  ) || mockData.serviceCategories[0];

  // Split services into 2 columns
  const servicesPerColumn = Math.ceil(activeCategoryData.services.length / 2);
  const column1Services = activeCategoryData.services.slice(0, servicesPerColumn);
  const column2Services = activeCategoryData.services.slice(servicesPerColumn);

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {/* Trigger Button */}
      <button 
        className="font-semibold px-4 py-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300 flex items-center"
      >
        Services 
        <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Mega Menu Dropdown */}
      {isOpen && (
        <>
        <div className="absolute top-full left-0 w-full h-4" />
        <div 
          className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50"
          style={{
            width: '1100px',
            maxWidth: 'calc(100vw - 3rem)'
          }}
        >
          <div className="bg-white shadow-2xl rounded-xl overflow-hidden">
          <div className="flex">
            {/* Left Sidebar - Categories */}
            <div className="w-64 bg-gray-50 p-6 border-r border-gray-200">
              <div className="space-y-2">
                {mockData.serviceCategories.map((category) => (
                  <button
                    key={category.id}
                    onMouseEnter={() => setActiveCategory(category.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${
                      activeCategory === category.id
                        ? 'bg-secondary text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs">▶</span>
                      {category.name}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Right Panel - Services in 2 Columns */}
            <div className="flex-1 p-8">
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                {/* Column 1 */}
                <div className="space-y-4">
                  {column1Services.map((serviceSlug) => {
                    const service = mockData.services.find(s => s.slug === serviceSlug);
                    if (!service) return null;
                    
                    return (
                      <Link
                        key={service.slug}
                        to={`/services/${service.slug}`}
                        className="block group"
                        onClick={() => setIsOpen(false)}
                      >
                        <div className="flex items-start gap-2">
                          <span className="text-secondary mt-1 text-xs">▶</span>
                          <div>
                            <div className="font-bold text-secondary text-sm group-hover:underline">
                              {service.title}
                            </div>
                            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                              {service.shortDesc}
                            </p>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>

                {/* Column 2 */}
                <div className="space-y-4">
                  {column2Services.map((serviceSlug) => {
                    const service = mockData.services.find(s => s.slug === serviceSlug);
                    if (!service) return null;
                    
                    return (
                      <Link
                        key={service.slug}
                        to={`/services/${service.slug}`}
                        className="block group"
                        onClick={() => setIsOpen(false)}
                      >
                        <div className="flex items-start gap-2">
                          <span className="text-secondary mt-1 text-xs">▶</span>
                          <div>
                            <div className="font-bold text-secondary text-sm group-hover:underline">
                              {service.title}
                            </div>
                            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                              {service.shortDesc}
                            </p>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Expert Legal Support Section - Right Side */}
            <div className="w-72 bg-gradient-to-br from-gray-900 to-black text-white p-8 flex flex-col items-center text-center relative overflow-hidden">
              {/* Abstract Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-10 left-10 w-32 h-32 bg-secondary rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-40 h-40 bg-secondary/60 rounded-full blur-3xl"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-secondary/40 rounded-full blur-3xl"></div>
              </div>
              
              <div className="relative z-10 w-full flex flex-col items-center">
              {/* Icon */}
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
                <Users className="w-8 h-8" />
              </div>

              {/* Heading */}
              <h3 className="text-xl font-bold mb-2">Expert Legal Support</h3>
              <p className="text-sm opacity-90 mb-6">Professional attorneys guiding you every step</p>

              {/* Benefits */}
              <div className="space-y-3 mb-8 w-full">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-secondary" />
                  </div>
                  <span className="text-sm font-medium">Free Consultation</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-secondary" />
                  </div>
                  <span className="text-sm font-medium">24/7 Support</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-secondary" />
                  </div>
                  <span className="text-sm font-medium">95% Success Rate</span>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={() => {
                  setIsPopupOpen(true);
                  setIsOpen(false);
                }}
                className="w-full bg-secondary hover:bg-secondary/90 text-white font-bold px-6 py-3 rounded-lg transition-all hover:scale-105 flex items-center justify-center gap-2 shadow-lg"
              >
                Get Started Now
                <span>→</span>
              </button>
              </div>
            </div>
          </div>
          </div>
        </div>
        </>
      )}

      {/* Popup */}
      <Popup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default MegaMenu;
